import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select, DatePicker } from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import {
  GetProductDataAPI,
  GetCustomerDataAPI,
  CreateOrdersDataAPI,
  GetOrdersDataAPI,
  UpdateOrdersDataAPI,
  DeleteOrdersDataAPI,
  GetProductRateDataAPI,
} from '../../AllAPI/AdminAPI'

const Orders = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))
  /* ================= LIST ================= */
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState('')

  /* ================= MASTER ================= */
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])

  /* ================= MODALS ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingOrderId, setEditingOrderId] = useState(null)

  /* ================= HEADER INPUT ================= */
  const [orderDate, setOrderDate] = useState(null)
  const [customerId, setCustomerId] = useState(null)

  /* ================= PRODUCT INPUT ================= */
  const [productId, setProductId] = useState(null)
  const [qty, setQty] = useState('')
  const [rate, setRate] = useState('')

  /* ================= TEMP ITEMS ================= */
  const [items, setItems] = useState([])

  /* ================= DELETE MODAL ================= */
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteOrderId, setDeleteOrderId] = useState(null)

  const [submitLoading, setSubmitLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [actionLoadingId, setActionLoadingId] = useState(null)

  useEffect(() => {
    fetchCustomers()
    fetchProducts()
    fetchOrders()
  }, [])

  /* ================= FETCH ================= */
  const fetchCustomers = async () => {
    const res = await axiosInstance.get(`${GetCustomerDataAPI}/${selectedYear}`)
    setCustomers(res.data || [])
  }

  const fetchProducts = async () => {
    const res = await axiosInstance.get(
      `${GetProductRateDataAPI}/${selectedYear}`,
    )
    setProducts(res.data || [])
  }

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get(`${GetOrdersDataAPI}/${selectedYear}`)
      const data = Array.isArray(res.data) ? res.data : []
      setOrders(data)
      setFilteredOrders(data)
    } catch {
      // toast.error('Failed to load order data')
    } finally {
      setLoading(false)
    }
  }

  /* ================= SEARCH ================= */
  const handleSearch = (value) => {
    setSearchText(value)
    const filtered = orders.filter((o) =>
      `${o.customer_name} ${o.date}`
        .toLowerCase()
        .includes(value.toLowerCase()),
    )
    setFilteredOrders(filtered)
  }

  /* ================= ADD PRODUCT ================= */
  const addProduct = () => {
    if (!orderDate) return toast.error('Select order date')
    if (!customerId) return toast.error('Select customer')
    if (!productId) return toast.error('Select product')
    if (!qty || qty <= 0) return toast.error('Enter valid qty')
    if (!rate || rate <= 0) return toast.error('Enter valid rate')

    if (items.find((i) => i.product_id === productId))
      return toast.error('Product already added')

    const product = products.find((p) => p.prod_id === productId)

    const total = Number(qty) * Number(rate)

    setItems([
      ...items,
      {
        product_id: productId,
        product_name: product.product.product_name,
        qty: Number(qty),
        rate: Number(rate),
        total_amount: total,
      },
    ])

    setProductId(null)
    setQty('')
    setRate('')
  }

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index))
  }

  // ==== For Add Modal==============
  const grossTotal = items.reduce((sum, i) => sum + Number(i.total_amount), 0)
  const grossQty = items.reduce((sum, i) => sum + Number(i.qty), 0)

  // ==== For View Modal=============
  const totalQty = (selectedOrder?.items || []).reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  )

  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
    if (!orderDate) return toast.error('Select order date')
    if (!customerId) return toast.error('Select customer')
    if (!items.length) return toast.error('Add at least one product')

    const payload = {
      date: orderDate.format('YYYY-MM-DD'),
      cust_id: customerId,
      gross_total: grossTotal,
      year: selectedYear,
      items: items.map((i) => ({
        prod_id: i.product_id,
        quantity: i.qty,
        rate: i.rate,
      })),
    }

    try {
      setSubmitLoading(true)
      let res

      if (isEditMode) {
        //  UPDATE API
        res = await axiosInstance.put(
          `${UpdateOrdersDataAPI}/${editingOrderId}`,
          payload,
        )
      } else {
        //  CREATE API
        res = await axiosInstance.post(CreateOrdersDataAPI, payload)
      }

      if (res.data === 'SAVED' || res.data === 'UPDATED') {
        toast.success(isEditMode ? 'Order updated' : 'Order saved')
        setIsModalOpen(false)
        resetForm()
        fetchOrders()
      }
    } catch (error) {
      toast.error('Operation failed')
      console.error(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const resetForm = () => {
    setOrderDate(null)
    setCustomerId(null)
    setProductId(null)
    setQty('')
    setRate('')
    setItems([])
    setIsEditMode(false)
    setEditingOrderId(null)
  }

  // =========== EDIT FUN ==============

  const handleEdit = (record) => {
    setIsEditMode(true)
    setEditingOrderId(record.order_id)
    setOrderDate(dayjs(record.date))
    setCustomerId(record.cust_id)

    setItems(
      record.orders.map((i) => ({
        product_id: i.prod_id,
        product_name: i.product_name,
        qty: i.quantity,
        rate: i.rate,
        total_amount: i.quantity * i.rate,
      })),
    )

    setIsModalOpen(true)
  }

  //=========== DELETE FUN ============

  const openDeleteModal = (record) => {
    const orderId = record

    setDeleteOrderId(orderId)
    setDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteOrderId) return

    try {
      setDeleteLoading(true)

      await axiosInstance.delete(`${DeleteOrdersDataAPI}/${deleteOrderId}`)
      toast.success('Order deleted successfully')
      setDeleteModal(false)
      setDeleteOrderId(null)
      fetchOrders()
    } catch (error) {
      toast.error('Failed to delete order')
      console.error(error)
    } finally {
      setDeleteLoading(false)
    }
  }

  const handleProductChange = (value) => {
    setProductId(value)

    const selectedProduct = products.find((p) => p.prod_id === value)

    if (selectedProduct) {
      setRate(selectedProduct.rate || '')
    }
  }

  /* ================= TABLES ================= */
  const orderColumns = [
    {
      title: 'Sr No',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Order Date',
      dataIndex: 'date',
      render: (d) => dayjs(d).format('DD-MM-YYYY'),
    },

    { title: 'Customer Name', dataIndex: 'customer_name' },
    {
      title: 'Total Qty',
      render: (_, record) => {
        const totalQty = (record.orders || []).reduce(
          (sum, o) => sum + Number(o.quantity || 0),
          0,
        )

        return <div style={{ textAlign: 'right' }}>{totalQty}</div>
      },
    },

    {
      title: 'Gross Total (₹)',
      dataIndex: 'gross_total',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    // {
    //   title: 'Action',
    //   render: (_, record) => (
    //     <>
    //       <Button
    //         type="text"
    //         icon={<EditOutlined />}
    //         onClick={() => handleEdit(record)}
    //         title="Edit"
    //       />

    //       <Button
    //         type="text"
    //         icon={<EyeOutlined />}
    //         title="View"
    //         onClick={() => {
    //           const mappedOrder = {
    //             ...record,
    //             items: record.orders.map((o, index) => ({
    //               key: `${record.order_id}-${o.order_id}-${index}`,
    //               product_name: o.product_name,
    //               qty: o.quantity,
    //               rate: o.rate,
    //               total_amount: o.quantity * o.rate,
    //             })),
    //           }

    //           setSelectedOrder(mappedOrder)
    //           setViewModal(true)
    //         }}
    //       />

    //       <Button
    //         type="text"
    //         danger
    //         icon={<DeleteOutlined />}
    //         title="Delete"
    //         onClick={() => openDeleteModal(record)}
    //       />
    //     </>
    //   ),
    // },
    {
      title: 'Action',
      render: (_, record) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            loading={actionLoadingId === `edit-${record.order_id}`}
            onClick={() => {
              setActionLoadingId(`edit-${record.order_id}`)
              handleEdit(record)
              setTimeout(() => setActionLoadingId(null), 300)
            }}
            title="Edit"
          />

          <Button
            type="text"
            icon={<EyeOutlined />}
            loading={actionLoadingId === `view-${record.order_id}`}
            title="View"
            onClick={() => {
              setActionLoadingId(`view-${record.order_id}`)

              const mappedOrder = {
                ...record,
                items: record.orders.map((o, index) => ({
                  key: `${record.order_id}-${o.order_id}-${index}`,
                  product_id: o.prod_id,
                  product_name: o.product_name,
                  qty: o.quantity,
                  rate: o.rate,
                  total_amount: o.quantity * o.rate,
                })),
              }

              setSelectedOrder(mappedOrder)
              setViewModal(true)

              setTimeout(() => setActionLoadingId(null), 300)
            }}
          />

          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            loading={actionLoadingId === `delete-${record.order_id}`}
            title="Delete"
            onClick={() => {
              setActionLoadingId(`delete-${record.order_id}`)
              openDeleteModal(record.order_id)
              setTimeout(() => setActionLoadingId(null), 300)
            }}
          />
        </>
      ),
    },
  ]

  const tempColumns = [
    { title: 'Product Id', dataIndex: 'product_id' },
    { title: 'Product', dataIndex: 'product_name' },
    {
      title: 'Qty',
      dataIndex: 'qty',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
    {
      title: 'Rate (₹)',
      dataIndex: 'rate',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
    {
      title: 'Total Amt (₹)',
      dataIndex: 'total_amount',
      render: (value) => (
        <div style={{ textAlign: 'right' }}> {Number(value)}</div>
      ),
    },
    {
      title: 'Action',
      render: (_, __, index) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeItem(index)}
        />
      ),
    },
  ]

  const viewColumns = [
    { title: 'Product Id', dataIndex: 'product_id' },
    { title: 'Product', dataIndex: 'product_name' },
    {
      title: 'Qty',
      dataIndex: 'qty',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
    {
      title: 'Rate (₹)',
      dataIndex: 'rate',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
    {
      title: 'Total (₹)',
      dataIndex: 'total_amount',
      render: (value) => (
        <div style={{ textAlign: 'right' }}>{Number(value)}</div>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-row">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Orders
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => (setIsModalOpen(true), resetForm())}
          >
            Add
          </Button>

          <Input
            placeholder="Search customer / date"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={orderColumns}
          dataSource={filteredOrders}
          rowKey={(r) => `${r.date}-${r.cust_id}`}
          loading={loading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= ADD MODAL ================= */}
      <Modal
        title="Order Entry"
        open={isModalOpen}
        onCancel={() => (setIsModalOpen(false), resetForm())}
        onOk={handleSubmit}
        okText="Save"
        width={900}
        okButtonProps={{
          loading: submitLoading,
          className: 'receipt-ok-btn',
        }}
      >
        <div className="row">
          <div className="col-md-4 py-1">
            <DatePicker
              value={orderDate}
              onChange={setOrderDate}
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-md-8 py-1">
            <Select
              value={customerId}
              onChange={setCustomerId}
              placeholder="Select Customer"
              style={{ width: '100%' }}
            >
              {customers.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.customer_name}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <hr />

        <div className="row">
          <div className="col-md-6 py-1">
            <Select
              value={productId}
              onChange={handleProductChange}
              placeholder="Product"
              style={{ width: '100%' }}
            >
              {products.map((p) => (
                <Select.Option key={p.prod_id} value={p.prod_id}>
                  {p.product.product_name} &nbsp; ({p.product.size})
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="col-md-2 py-1">
            <Input
              placeholder="Qty"
              type="number"
              value={qty}
              step="1"
              onKeyDown={(e) => {
                if (e.key === '.' || e.key === ',') {
                  e.preventDefault()
                }
              }}
              onChange={(e) => setQty(e.target.value)}
            />
          </div>

          <div className="col-md-2 py-1">
            <Input
              placeholder="Rate"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>

          <div className="col-md-2 py-1">
            <Button type="primary" onClick={addProduct}>
              Add
            </Button>
          </div>
        </div>

        <Table
          className="mt-3 shadow"
          columns={tempColumns}
          dataSource={items}
          rowKey={(_, index) => index}
          pagination={false}
          style={{ overflowX: 'auto' }}
        />

        <div className="row mt-2">
          <div className="col-12 col-md-4"></div>
          <div className="col-12 col-md-4 text-md-end mt-2">
            <span className="py-1 px-2">
              Total Qty: <b> {grossQty}</b>
            </span>
          </div>
          <div className="col-12 col-md-4 text-md-end mt-2">
            <span className="py-1 px-2" style={{ backgroundColor: 'yellow' }}>
              Total Amt (₹): <b> {grossTotal}</b>
            </span>
          </div>
        </div>
      </Modal>

      {/* ================= VIEW MODAL ================= */}
      <Modal
        title="Order Details"
        open={viewModal}
        onCancel={() => (setViewModal(false), setSelectedOrder(null))}
        footer={null}
        width={700}
        okButtonProps={{
          className: 'receipt-ok-btn',
        }}
      >
        <Table
          columns={viewColumns}
          dataSource={selectedOrder?.items || []}
          rowKey="key"
          pagination={false}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
        <div className="row">
          <div className="col-12 col-md-4"></div>
          <div className="col-12 col-md-4 text-md-end mt-2">
            <span className="py-1 px-2">
              Total Qty : <b>{totalQty}</b>
            </span>
          </div>
          <div className="col-12 col-md-4 text-md-end mt-2">
            <span className="py-1 px-2" style={{ backgroundColor: 'yellow' }}>
              Gross Total : <b>₹ {selectedOrder?.gross_total}</b>
            </span>
          </div>
        </div>
      </Modal>

      {/* ================= DELETE MODAL ================= */}
      <Modal
        title="Confirm Delete"
        open={deleteModal}
        onCancel={() => {
          setDeleteModal(false)
          setDeleteOrderId(null)
        }}
        onOk={handleDeleteConfirm}
        okButtonProps={{
          loading: deleteLoading,
        }}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure you want to delete this order?</p>
      </Modal>
    </>
  )
}

export default Orders
